<template>
  <el-input
    v-model="inputValue"
    :placeholder="placeholder"
    :maxlength="maxlength"
    :inputmode="canInputDecimal ? 'decimal' : 'numeric'"
    @input="handleInput"
    @keydown="handleKeyDown"
    @change="handleChange"
  >
    <template #prefix>
      <slot name="prefix"></slot>
    </template>
    <template #suffix>
      <slot name="suffix"></slot>
    </template>
  </el-input>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  placeholder: {
    type: String,
    default: '请输入',
  },
  maxlength: {
    type: [String, Number],
  },
  // 是否允许输入小数
  allowDecimal: {
    type: Boolean,
    default: false,
  },
  // 小数位数，仅 allowDecimal 为 true 时生效
  decimalPlaces: {
    type: [String, Number],
    default: 2,
  },
})

const emit = defineEmits(['update:modelValue', 'change', 'input', 'keydown'])

const inputValue = ref(props.modelValue)
const normalizedDecimalPlaces = computed(() => {
  const places = Number(props.decimalPlaces)
  return Number.isFinite(places) ? Math.max(0, Math.floor(places)) : 2
})
const canInputDecimal = computed(
  () => props.allowDecimal && normalizedDecimalPlaces.value > 0
)

const sanitizeValue = (value: string | number) => {
  const source = String(value ?? '')
  if (!canInputDecimal.value) return source.replace(/\D/g, '')

  const decimalPointIndex = source.indexOf('.')
  if (decimalPointIndex < 0) return source.replace(/\D/g, '')

  const integerPart = source.slice(0, decimalPointIndex).replace(/\D/g, '')
  const decimalPart = source
    .slice(decimalPointIndex + 1)
    .replace(/\D/g, '')
    .slice(0, normalizedDecimalPlaces.value)

  return `${integerPart || '0'}.${decimalPart}`
}

// 监听父组件传入的值变化
watch(
  () => props.modelValue,
  newVal => {
    inputValue.value = newVal
  }
)

// 输入规则动态变化时，同步清理已有值
watch([canInputDecimal, normalizedDecimalPlaces], () => {
  const filteredValue = sanitizeValue(inputValue.value)
  if (String(inputValue.value ?? '') === filteredValue) return
  inputValue.value = filteredValue
  emit('update:modelValue', filteredValue)
  emit('input', filteredValue)
})

// 处理输入，过滤非法字符及超出限制的小数位
const handleInput = (value: string | number) => {
  const filteredValue = sanitizeValue(value)
  inputValue.value = filteredValue
  emit('update:modelValue', filteredValue)
  emit('input', filteredValue)
}

// 阻止非数字键的默认行为
const handleKeyDown = (e: KeyboardEvent) => {
  emit('keydown', e)
  const allowedKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'Tab',
    'Home',
    'End',
    'Enter',
    'Insert',
  ]
  if (
    (e.ctrlKey || e.metaKey) &&
    ['a', 'c', 'v', 'x', 'y', 'z'].includes(e.key.toLowerCase())
  )
    return
  if (allowedKeys.includes(e.key)) return

  if (/^\d$/.test(e.key)) {
    if (!canInputDecimal.value) return

    const input = e.target as HTMLInputElement
    const dotIndex = input.value.indexOf('.')
    const selectionStart = input.selectionStart ?? input.value.length
    const selectionEnd = input.selectionEnd ?? selectionStart
    const isReplacingText = selectionEnd > selectionStart
    if (
      dotIndex >= 0 &&
      selectionStart > dotIndex &&
      !isReplacingText &&
      input.value.length - dotIndex - 1 >= normalizedDecimalPlaces.value
    ) {
      e.preventDefault()
    }
    return
  }

  if (e.key === '.' && canInputDecimal.value) {
    const input = e.target as HTMLInputElement
    const selectedText = input.value.slice(
      input.selectionStart ?? 0,
      input.selectionEnd ?? 0
    )
    if (!input.value.includes('.') || selectedText.includes('.')) return
  }

  e.preventDefault()
}

// 处理值变化
const handleChange = value => {
  emit('change', value)
}
</script>
